/*
 *
 * App reducer
 *
 */

import { fromJS } from 'immutable';
import find from 'lodash/find';
import * as routes from '../../constants/routes';
import produce from 'immer';
import moment from 'moment';
import CommonFunctionsUtils from '../../utils/CommonFunctionsUtils';
import { DEFAULT_LOCALE } from './constants';

import {
  DEFAULT_ACTION,
  AUTH_USER_SET,
  AUTH_USER_SET_SUCCESS,
  DOCKET_SHOP_VALIDATION_FAILURE,
  LOAD_USER_PROFILE,
  LOAD_INVOICES_SUCCESS_ACTION,
  LOAD_COMPANY_ACTION,
  LOAD_COMPANY_SUCCESS_ACTION,
  LOAD_COMPANY_FAILURE_ACTION,
  UPDATE_COMPANY_ACTION,
  UPDATE_COMPANY_SUCCESS_ACTION,
  UPDATE_COMPANY_FAILURE_ACTION,
  UPDATE_PLAN_ACTION,
  UPDATE_PLAN_SUCCESS_ACTION,
  UPDATE_PLAN_FAILURE_ACTION,
  SAVE_WORKFLOW,
  SAVE_WORKFLOW_SUCCESS,
  SAVE_WORKFLOW_FAILURE,
  FETCH_WORKFLOWS,
  FETCH_WORKFLOWS_SUCCESS,
  FETCH_WORKFLOWS_FAILURE,
  LOAD_CUSTOMERS_ACTION,
  LOAD_CUSTOMERS_SUCCESS_ACTION,
  LOAD_CUSTOMERS_FAILURE_ACTION,
  CREATE_CUSTOMER_ACTION,
  CREATE_CUSTOMER_SUCCESS_ACTION,
  CREATE_CUSTOMER_FAILURE_ACTION,
  UPDATE_CUSTOMER_ACTION,
  UPDATE_CUSTOMER_SUCCESS_ACTION,
  UPDATE_CUSTOMER_FAILURE_ACTION,
  DELETE_CART_CONFIGURATION,
  DELETE_CART_CONFIGURATION_SUCCESS,
  DELETE_CART_CONFIGURATION_FAILURE,
  SAVE_EMBED_CONFIGURATION,
  SAVE_EMBED_CONFIGURATION_SUCCESS,
  SAVE_EMBED_CONFIGURATION_FAILURE,
  SAVE_CART_CONFIGURATION,
  SAVE_CART_CONFIGURATION_SUCCESS,
  SAVE_CART_CONFIGURATION_FAILURE,
  FETCH_CART_CONFIGURATIONS,
  FETCH_CART_CONFIGURATIONS_SUCCESS,
  FETCH_CART_CONFIGURATIONS_FAILURE,
  FETCH_EMBED_CONFIGURATIONS,
  FETCH_EMBED_CONFIGURATIONS_SUCCESS,
  SYNC_EMBED_CONFIGURATIONS,
  SYNC_CART_CONFIGURATIONS,
  SIGN_OUT,
  LOAD_PERMISSIONS_ACTION,
  LOAD_PERMISSIONS_SUCCESS_ACTION,
  LOAD_PERMISSIONS_FAILURE_ACTION,
  LOAD_AD_HOC_ENTITY,
  LOAD_AD_HOC_ENTITY_SUCCESS,
  LOAD_AD_HOC_INVOICE,
  LOAD_MESSAGES,
  LOAD_LOGS,
  ADD_LOG,
  SUBSCRIBE_ACTION,
  SUBSCRIBE_SUCCESS_ACTION,
  SUBSCRIBE_FAILURE_ACTION,
  STRIPE_CONNECT_ACTION,
  STRIPE_CONNECT_SUCCESS_ACTION,
  STRIPE_CONNECT_FAILURE_ACTION,
  LOAD_CC_GATEWAY_ACTION,
  LOAD_CC_GATEWAY_SUCCESS_ACTION,
  LOAD_CC_GATEWAY_FAILURE_ACTION,
  LOAD_PAYMENTS,
  LOAD_COMPANY_PLAN_SUCCESS_ACTION,
  LOAD_DOCUMENTS_ACTION,
  LOAD_DOCUMENT_INSTANCES_ACTION,
  UPDATE_USER_CONFIGS,
  LOAD_USER_CONFIGS_SUCCESS,
  UPDATE_USER_CONFIGS_SUCCESS,
  LOAD_UNPAID_INVOICES_ACTION,
  LOAD_INTEGRATIONS_ACTION,
  LOAD_COMPANY_MEMBERS_ACTION,
  LOAD_COMPANY_MEMBERS_SUCCESS_ACTION,
  LOAD_UNASSIGNED_INVOICES_SUCCESS_ACTION,
  LOAD_PROFILE_SUCCESS_ACTION,
  QB_SYNC_FINSISHED_ACTION,
  QB_SYNC_INVOICES_ACTION,
  QB_SYNC_CUSTOMERS_ACTION,
  FETCH_PRODUCTS,
  FETCH_PRODUCTS_SUCCESS,
  FETCH_PRODUCTS_FAILURE,
  FETCH_VARIANTS,
  FETCH_VARIANTS_SUCCESS,
  FETCH_VARIANTS_FAILURE,
  DELETE_VARIANT,
  DELETE_VARIANT_SUCCESS,
  DELETE_VARIANT_FAILURE,
  CREATE_INVOICE_SUCCESS_ACTION,
  UPDATE_INVOICE_SUCCESS_ACTION,
  FETCH_CONNECTED_ACCOUNTS_SUCCESS,
  FETCH_CONNECTED_ACCOUNTS_FAILURE,
  FETCH_CONNECTED_COMPANIES_SUCCESS,
  SAVE_CONNECTED_ACCOUNT,
  SAVE_CONNECTED_ACCOUNT_SUCCESS,
  SAVE_CONNECTED_ACCOUNT_FAILURE,
  FETCH_DISCOUNTS,
  FETCH_DISCOUNTS_FAILURE,
  FETCH_DISCOUNTS_SUCCESS,
  DELETE_DISCOUNT,
  DELETE_DISCOUNT_FAILURE,
  DELETE_DISCOUNT_SUCCESS,
  SAVE_DISCOUNT,
  SAVE_DISCOUNT_FAILURE,
  SAVE_DISCOUNT_SUCCESS,
  FETCH_API_KEYS_SUCCESS,
  SET_MAX_DAILY_BOOKABLE_TASKS,
} from './constants';

export const initialState = {
  debounceStart: 0,
  qbSynching: false,
  authUser: null,
  docketShopValidationError: undefined,
  profile: {},
  user: {},
  company: {},
  loadingCompany: false,
  userConfigs: {},
  loadingCompanyError: false,
  invitation: {},
  customers: {},
  loadingCustomers: false,
  loadingCustomersError: false,
  invoices: {},
  creatingCustomer: false,
  creatingCustomerError: false,
  updatingCustomer: false,
  updatingCustomerError: false,
  selectedCompany: undefined,
  integrations: {},
  loadingPermissions: false,
  loadingPermissionsFailure: false,
  payments: {},
  documents: {},
  documentInstances: {},
  plan: {},
  ccGateway: {},
  products: {},
  discounts: {},
  variants: {},
  workflows: {},
  connectedAccounts: {},
  connectedCompanies: {},
  apiMap: {},
  embedded: CommonFunctionsUtils.isAppEmbedded(),
  notificationProperties: {
    invoiceSent: {
      id: 'invoiceSent',
      group: 'Client',
      subGroup: 'Financial Notifications',
      canEditMessage: true,
      canAttach: true,
      attach: false,
      label: 'Invoice Sent',
      description:
        'When an invoice is sent to a client this notification will be sent to the client. You also have the option to attach the invoice to the email.',
    },
    invoicePaid: {
      id: 'invoicePaid',
      group: 'Employee',
      subGroup: 'Invoice Notifications',
      canEditMessage: false,
      includeDefaultEmailAddress: false,
      includeDefaultPhoneNumber: false,
      label: 'Invoice Paid',
      description:
        'When an invoice is paid this notification will be sent to you.',
    },
    invoiceSentEmployee: {
      id: 'invoiceSentEmployee',
      group: 'Employee',
      subGroup: 'Invoice Notifications',
      canEditMessage: false,
      includeDefaultEmailAddress: false,
      includeDefaultPhoneNumber: false,
      label: 'Invoice Sent',
      description:
        'When an invoice is sent to a client this notification will be sent to you.',
    },
    paymentRecieved: {
      id: 'paymentRecieved',
      group: 'Client',
      subGroup: 'Financial Notifications',
      canEditMessage: false,
      canAttach: true,
      label: 'Payment Receipt',
      description:
        'When you recieve a payment from the client this notification will be sent to the client. You also have the option to attach the invoice to the email.',
    },
    paymentRequested: {
      id: 'paymentRequested',
      group: 'Client',
      subGroup: 'Financial Notifications',
      canEditMessage: false,
      label: 'Payment Requested',
      description:
        'When you request a payment from the client this notification will be sent to the client.',
    },
    documentSent: {
      id: 'documentSent',
      group: 'Client',
      subGroup: 'Document Notifications',
      canEditMessage: false,
      label: 'Document Sent',
      description:
        'When a document is sent to view and/or sign this notification will be sent to the client.',
    },
  },
  history: [],
  unpaidInvoices: {},
  referrals: {},
  isConnectingStripe: false,
  cartConfigurations: {},
  embedConfigurations: {},
  fetchingCartConfigurations: false,
  fetchingWorkflows: false,
  fetchingEmbedConfigurations: false,
  coupons: {
    '50OFF': {
      code: '50OFF',
      amount: 50,
      startDate: moment('20210201')
        .startOf('day')
        .valueOf(),
      endDate: moment('20210228')
        .endOf('day')
        .valueOf(),
    },
    SPRING: {
      code: 'SPRING',
      amount: 75,
      startDate: moment('20210301')
        .startOf('day')
        .valueOf(),
      endDate: moment('20210331')
        .endOf('day')
        .valueOf(),
    },
    SAVE55: {
      code: 'SAVE55',
      amount: 55,
      startDate: moment('20210527')
        .startOf('day')
        .valueOf(),
      endDate: moment('20210630')
        .endOf('day')
        .valueOf(),
    },
  },
  productName: 'DocketShop',
};

export default produce((draft, action) => {
  // function appReducer(initialState, action) {
  let data = undefined;
  switch (action.type) {
    case FETCH_API_KEYS_SUCCESS: {
      draft.apiMap = action.apiMap;
      break;
    }
    case FETCH_CART_CONFIGURATIONS: {
      draft.fetchingCartConfigurations = true;
      break;
    }
    case FETCH_CART_CONFIGURATIONS_SUCCESS: {
      draft.cartConfigurations = action.cartConfigurations;
      draft.fetchingCartConfigurations = false;
      break;
    }
    case FETCH_CART_CONFIGURATIONS_FAILURE: {
      draft.fetchingCartConfigurations = false;
      break;
    }
    case SAVE_CART_CONFIGURATION: {
      break;
    }
    case SAVE_CART_CONFIGURATION_SUCCESS: {
      break;
    }
    case SAVE_CART_CONFIGURATION_FAILURE: {
      break;
    }
    case SAVE_EMBED_CONFIGURATION: {
      break;
    }
    case SAVE_EMBED_CONFIGURATION_SUCCESS: {
      break;
    }
    case SAVE_EMBED_CONFIGURATION_FAILURE: {
      break;
    }
    case FETCH_EMBED_CONFIGURATIONS: {
      draft.fetchingEmbedConfigurations = true;
      break;
    }
    case FETCH_EMBED_CONFIGURATIONS_SUCCESS: {
      draft.fetchingEmbedConfigurations = false;
      draft.embedConfigurations = action.embedConfigurations;
      break;
    }
    case FETCH_WORKFLOWS_FAILURE: {
      draft.fetchingEmbedConfigurations = false;
      break;
    }
    case LOAD_PROFILE_SUCCESS_ACTION:
      draft.user = action.userProfile;
      return;
    case LOAD_UNPAID_INVOICES_ACTION:
      window.updateStats(
        LOAD_UNPAID_INVOICES_ACTION,
        Object.keys(action.invoices).length,
      );
      draft.unpaidInvoices = action.invoices;
      return;
    case LOAD_INTEGRATIONS_ACTION:
      window.updateStats(LOAD_INTEGRATIONS_ACTION, 1);
      draft.integrations = action.integrations;
      return;
    case LOAD_USER_CONFIGS_SUCCESS:
      window.updateStats(LOAD_USER_CONFIGS_SUCCESS, 1);
      draft.userConfigs = action.configs;
      return;
    case LOAD_PAYMENTS:
      window.updateStats(LOAD_PAYMENTS, Object.keys(action.payments).length);
      draft.payments = action.payments;
      return;
    case SIGN_OUT:
      return initialState; // or just set profile to {}
    case AUTH_USER_SET:
      draft.loadingProfileAuthUser = action.authUser;
      return;
    case AUTH_USER_SET_SUCCESS:
      draft.authUser = action.authUser;
      return;
    case DOCKET_SHOP_VALIDATION_FAILURE:
      draft.docketShopValidationError = action.reason;
      return;
    case LOAD_USER_PROFILE:
      window.updateStats(LOAD_USER_PROFILE, 1);
      if (action.profile === undefined) return;
      if (action.profile.companies) {
        let selectedCompany = undefined;

        if (action.profile.selectedCompany) {
          selectedCompany =
            action.profile.companies[action.profile.selectedCompany];
        } else if (Object.keys(action.profile.companies).length > 0) {
          let key = Object.keys(action.profile.companies)[0];
          selectedCompany = action.profile.companies[key];
        }

        draft.selectedCompany = selectedCompany;
      }
      draft.profile = action.profile;
      return;
    case DEFAULT_ACTION:
      return;
    case SUBSCRIBE_ACTION:
      draft.isSubscribing = true;
      draft.subscriptionError = false;
      draft.subscriptionErrorType = undefined;
      return;
    case SUBSCRIBE_SUCCESS_ACTION:
      draft.isSubscribing = false;
      draft.subscriptionError = false;
      draft.subscriptionErrorType = undefined;
      return;
    case SUBSCRIBE_FAILURE_ACTION:
      draft.isSubscribing = false;
      draft.subscriptionError = true;
      draft.subscriptionErrorType = action.error;
      return;
    case STRIPE_CONNECT_ACTION:
      draft.isConnectingStripe = true;
      draft.stripeConnectError = false;
      return;
    case STRIPE_CONNECT_SUCCESS_ACTION:
      draft.isConnectingStripe = false;
      draft.stripeConnectError = false;
      return;
    case STRIPE_CONNECT_FAILURE_ACTION:
      draft.isConnectingStripe = false;
      draft.stripeConnectError = action.error;
      return;
    case LOAD_CC_GATEWAY_ACTION:
      draft.isLoadingCCGatway = true;
      draft.loadingCCGatewayError = false;
      return;
    case LOAD_CC_GATEWAY_SUCCESS_ACTION:
      draft.ccGateway = action.ccGateway;
      draft.isLoadingCCGatway = false;
      draft.loadingCCGatewayError = false;
      return;
    case LOAD_CC_GATEWAY_FAILURE_ACTION:
      draft.isLoadingCCGatway = false;
      draft.loadingCCGatewayError = action.error;
      return;
    case LOAD_INVOICES_SUCCESS_ACTION:
      draft.invoices = action.invoices !== null ? action.invoices : {};
      window.updateStats(
        LOAD_INVOICES_SUCCESS_ACTION,
        Object.keys(action.invoices).length,
      );
      return;
    case UPDATE_INVOICE_SUCCESS_ACTION:
      let inv = { ...action.invoice };
      inv.waitingServerUpdate = true;
      draft.invoices[action.invoice.id] = inv;
      return;
    case CREATE_INVOICE_SUCCESS_ACTION:
      let inv2 = { ...action.invoice };
      inv2.waitingServerUpdate = true;
      draft.invoices[action.invoice.id] = inv2;
      return;
    case 'INVITATION':
      draft.invitation = action.invitation;
      draft.hasAccount = action.hasAccount;
      return;
    case LOAD_COMPANY_ACTION:
      draft.loadingCompany = true;
      draft.loadingCompanyError = false;
      return;
    case LOAD_COMPANY_MEMBERS_ACTION:
      draft.loadingMembers = true;
      draft.loadingCompanyMembersError = false;
      return;
    case LOAD_COMPANY_MEMBERS_SUCCESS_ACTION:
      draft.loadingMembers = false;
      draft.loadingCompanyMembersError = false;
      draft.members = action.members;
      window.updateStats(
        LOAD_COMPANY_MEMBERS_SUCCESS_ACTION,
        Object.keys(action.members).length,
      );

      return;
    case LOAD_COMPANY_SUCCESS_ACTION:
      window.updateStats(LOAD_COMPANY_SUCCESS_ACTION, 1);
      draft.loadingCompany = false;
      draft.loadingCompanyError = false;
      draft.company = action.company;
      draft.selectedCompany = action.company;
      if (action.memberProfile !== undefined) {
        draft.memberProfile = action.memberProfile;
      }
      return;
    case LOAD_COMPANY_PLAN_SUCCESS_ACTION:
      window.updateStats(LOAD_COMPANY_PLAN_SUCCESS_ACTION, 1);
      draft.plan = action.plan;
      return;
    case LOAD_COMPANY_FAILURE_ACTION:
      draft.loadingCompany = false;
      draft.loadingCompanyError = true;
      return;
    case UPDATE_COMPANY_ACTION:
      draft.updatingCompany = true;
      draft.updatingCompanyError = false;
      return;
    case UPDATE_COMPANY_SUCCESS_ACTION:
      window.updateStats(UPDATE_COMPANY_SUCCESS_ACTION, 1);
      draft.updatingCompany = false;
      draft.updatingCompanyError = false;
      draft.company = action.company;
      draft.selectedCompany = action.company;
      if (action.memberProfile !== undefined) {
        draft.memberProfile = action.memberProfile;
      }
      return;
    case UPDATE_COMPANY_FAILURE_ACTION:
      draft.updatingCompany = false;
      draft.updatingCompanyError = true;
      return;
    case UPDATE_PLAN_ACTION:
      draft.updatingPlan = true;
      draft.updatingPlanError = false;
      return;
    case UPDATE_COMPANY_SUCCESS_ACTION:
      draft.updatingPlan = false;
      draft.updatingPlanError = false;
      draft.plan = action.plan;
      return;
    case UPDATE_COMPANY_FAILURE_ACTION:
      draft.updatingPlan = false;
      draft.updatingPlanError = true;
      return;
    case LOAD_CUSTOMERS_ACTION:
      draft.loadingCustomers = true;
      draft.loadingCustomersError = false;
      return;
    case LOAD_CUSTOMERS_SUCCESS_ACTION:
      window.updateStats(
        LOAD_CUSTOMERS_SUCCESS_ACTION,
        Object.keys(action.customers).length,
      );
      draft.loadingCustomers = false;
      draft.loadingCustomersError = false;
      draft.customers = action.customers;
      return;
    case LOAD_CUSTOMERS_FAILURE_ACTION:
      draft.loadingCustomers = false;
      draft.loadingCustomersError = true;
      return;
    case CREATE_CUSTOMER_ACTION:
      draft.creatingCustomer = true;
      draft.creatingCustomerError = false;
      return;
    case CREATE_CUSTOMER_SUCCESS_ACTION:
      draft.creatingCustomer = false;
      draft.creatingCustomerError = false;
      draft.customers[action.customer.id] = action.customer;
      return;
    case CREATE_CUSTOMER_FAILURE_ACTION:
      draft.creatingCustomer = false;
      draft.creatingCustomerError = true;
      return;
    case UPDATE_CUSTOMER_ACTION:
      draft.updatingCustomer = true;
      draft.updatingCustomerError = false;
      return;
    case UPDATE_CUSTOMER_SUCCESS_ACTION:
      draft.updatingCustomer = false;
      draft.updatingCustomerError = false;
      draft.customers[action.customer.id] = action.customer;
      return;
    case UPDATE_CUSTOMER_FAILURE_ACTION:
      draft.updatingCustomer = false;
      draft.updatingCustomerError = true;
      return;
    case LOAD_PERMISSIONS_ACTION:
      draft.loadingPermissions = true;
      draft.loadingPermissionsError = false;
      return;
    case LOAD_PERMISSIONS_SUCCESS_ACTION:
      window.updateStats(
        LOAD_PERMISSIONS_SUCCESS_ACTION,
        Object.keys(action.permissions).length,
      );
      draft.loadingPermissions = false;
      draft.loadingPermissionsError = false;
      draft.permissions = action.permissions;
      return;
    case LOAD_PERMISSIONS_FAILURE_ACTION:
      draft.loadingPermissions = false;
      draft.loadingPermissionsError = true;
      return;
    case LOAD_DOCUMENTS_ACTION:
      draft.documents = action.documents;
      return;
    case LOAD_DOCUMENT_INSTANCES_ACTION:
      draft.documentInstances = action.documentInstances;
      return;
    case QB_SYNC_INVOICES_ACTION:
      draft.qbSynching = true;
      return;
    case QB_SYNC_CUSTOMERS_ACTION:
      draft.qbSynching = true;
      return;
    case QB_SYNC_FINSISHED_ACTION:
      draft.qbSynching = false;
      return;
    case FETCH_PRODUCTS:
      draft.fetchingProducts = true;
      return;
    case FETCH_PRODUCTS_SUCCESS:
      draft.fetchingProducts = false;
      console.log('fetching products...');
      draft.products = action.products;
      return;
    case FETCH_PRODUCTS_FAILURE:
      draft.fetchingProducts = false;
      draft.fetchingProductsError = true;
      return;
    case FETCH_DISCOUNTS:
      draft.fetchingDiscounts = true;
      return;
    case FETCH_DISCOUNTS_SUCCESS:
      draft.fetchingDiscounts = false;
      draft.discounts = action.discounts;
      return;
    case FETCH_DISCOUNTS_FAILURE:
      draft.fetchingDiscounts = false;
      draft.fetchingDiscountsError = true;
      return;
    case DELETE_DISCOUNT:
      return;
    case DELETE_DISCOUNT_SUCCESS:
      return;
    case DELETE_DISCOUNT_FAILURE:
      return;
    case SAVE_DISCOUNT: {
      break;
    }
    case SAVE_DISCOUNT_SUCCESS: {
      break;
    }
    case SAVE_DISCOUNT_FAILURE: {
      break;
    }
    case FETCH_VARIANTS:
      draft.fetchingVariants = true;
      return;
    case FETCH_VARIANTS_SUCCESS:
      draft.fetchingVariants = false;
      draft.variants[action.params.productId] = action.params.variants;
      return;
    case FETCH_VARIANTS_FAILURE:
      draft.fetchingVariants = false;
      draft.fetchingProductsError = true;
      return;
    case DELETE_VARIANT:
      return;
    case DELETE_VARIANT_SUCCESS:
      return;
    case DELETE_VARIANT_FAILURE:
      return;
    case SAVE_WORKFLOW: {
      break;
    }
    case SAVE_WORKFLOW_SUCCESS: {
      break;
    }
    case SAVE_WORKFLOW_FAILURE: {
      break;
    }
    case FETCH_WORKFLOWS: {
      draft.fetchingWorkflows = true;
      break;
    }
    case FETCH_WORKFLOWS_SUCCESS: {
      draft.fetchingWorkflows = false;
      draft.workflows = action.workflows;
      break;
    }
    case FETCH_WORKFLOWS_FAILURE: {
      draft.fetchingWorkflows = false;
      break;
    }
    case SAVE_CONNECTED_ACCOUNT: {
      break;
    }
    case FETCH_CONNECTED_ACCOUNTS_SUCCESS: {
      draft.connectedAccounts = action.connectedAccounts;
      break;
    }
    case FETCH_CONNECTED_COMPANIES_SUCCESS: {
      draft.connectedCompanies = action.connectedCompanies;
      break;
    }
    case SAVE_CONNECTED_ACCOUNT_SUCCESS: {
      break;
    }
    case SAVE_CONNECTED_ACCOUNT_FAILURE: {
      const { params } = action;
      console.log(params);
      break;
    }
    case SET_MAX_DAILY_BOOKABLE_TASKS: {
      const { maxDailyBookable } = action;
      if (!draft.company.taskProperties) draft.company.taskProperties = {};
      draft.company.taskProperties.maxDailyBookable = maxDailyBookable;
      break;
    }
    default:
      return;
  }
}, initialState);

// fetchWorkflows: params => dispatch(actions.fetchWorkflows(params)),
// syncWorkflows: params => dispatch(actions.syncWorkflows(params)),
// saveWorkflow: params => dispatch(actions.saveWorkflow(params)),
// deleteWorkflow: params => dispatch(actions.deleteWorkflow(params)),
